/**
 * The word-level diff behind the change card's long-text view (SEN-460): what a rewritten skill's
 * instructions (or any long markdown value) look like as ONE text with the changes inline — deleted words
 * struck through, inserted words highlighted — grouped into the text's markdown sections so unchanged
 * sections collapse to one line.
 *
 * Dependency-free: Myers' O(ND) diff, first over lines (so a 2000-word text with two edited sections
 * costs a few hundred comparisons), then over the words of each changed run of lines. A run too large to
 * diff word by word (a complete rewrite) is shown as deleted + inserted whole. Pure data out; the view is
 * TextDiffView.tsx.
 */

export type DiffOp = 'eq' | 'ins' | 'del'
export interface DiffSeg { op: DiffOp; text: string }

/** One line of the MERGED text (the new text with the deletions left in place). */
export interface DiffLine {
  segs: DiffSeg[]
  /** eq: untouched · changed: some words differ · ins: a new line · del: a removed line */
  kind: 'eq' | 'changed' | 'ins' | 'del'
}

/** A markdown section of the merged text: the lines from one heading to the next (the first has none). */
export interface DiffSection {
  /** the heading's words (new text; the old one when the heading was removed); '' before the first heading */
  title: string
  /** the old heading's words, when the heading itself was renamed */
  oldTitle?: string
  level: number
  lines: DiffLine[]
  changed: boolean
  added: number
  removed: number
}

export interface TextDiff {
  sections: DiffSection[]
  added: number
  removed: number
  changedSections: number
  /** false when old and new are the same text */
  changed: boolean
}

// ---- Myers ------------------------------------------------------------------------------------------

/** The shortest edit script from a to b (Myers 1986), as runs of eq/del/ins over the items' indices. */
export function myers<T>(a: readonly T[], b: readonly T[], eq: (x: T, y: T) => boolean = Object.is,
  maxD = Infinity): Array<{ op: DiffOp; a: number; b: number; n: number }> | null {
  const n = a.length
  const m = b.length
  const max = n + m
  const offset = max + 1
  const v = new Int32Array(2 * max + 3)
  // trace[d] keeps only v[-d..d] (what step d can read): O(D²) memory, not O(D·(N+M))
  const trace: Int32Array[] = []
  let done = max === 0
  for (let d = 0; d <= max && !done; d++) {
    if (d > maxD) return null
    trace.push(v.slice(offset - d, offset + d + 1))
    for (let k = -d; k <= d; k += 2) {
      let x = (k === -d || (k !== d && v[offset + k - 1]! < v[offset + k + 1]!)) ? v[offset + k + 1]! : v[offset + k - 1]! + 1
      let y = x - k
      while (x < n && y < m && eq(a[x]!, b[y]!)) { x++; y++ }
      v[offset + k] = x
      if (x >= n && y >= m) { done = true; break }
    }
  }
  // walk the trace back from (n, m)
  const steps: Array<{ op: DiffOp; a: number; b: number }> = []
  let x = n
  let y = m
  for (let d = trace.length - 1; d >= 0 && (x > 0 || y > 0); d--) {
    const vd = trace[d]!
    const at = (k: number) => vd[d + k] ?? 0
    const k = x - y
    const prevK = (k === -d || (k !== d && at(k - 1) < at(k + 1))) ? k + 1 : k - 1
    const prevX = at(prevK)
    const prevY = prevX - prevK
    while (x > prevX && y > prevY) { x--; y--; steps.push({ op: 'eq', a: x, b: y }) }
    if (d > 0) {
      if (x === prevX) { y--; steps.push({ op: 'ins', a: x, b: y }) } else { x--; steps.push({ op: 'del', a: x, b: y }) }
    }
  }
  while (x > 0 && y > 0) { x--; y--; steps.push({ op: 'eq', a: x, b: y }) }
  steps.reverse()
  const runs: Array<{ op: DiffOp; a: number; b: number; n: number }> = []
  for (const s of steps) {
    const last = runs[runs.length - 1]
    if (last && last.op === s.op) last.n++
    else runs.push({ op: s.op, a: s.a, b: s.b, n: 1 })
  }
  return runs
}

// ---- words ------------------------------------------------------------------------------------------

/** Words, whitespace runs, and markdown/punctuation marks as their own tokens ("**", "`", ",", "#"). */
export function tokenize(text: string): string[] {
  return text.match(/\s+|[\p{L}\p{N}_'’-]+|\*\*|[^\s\p{L}\p{N}]/gu) ?? []
}

const WORD = /[\p{L}\p{N}]/u
export function countWords(text: string): number {
  return tokenize(text).filter((t) => WORD.test(t)).length
}

/** Above this many edits a changed run is shown as removed + added whole (a rewrite, not an edit). */
const MAX_WORD_EDITS = 3000

function pushSeg(out: DiffSeg[], op: DiffOp, text: string) {
  if (!text) return
  const last = out[out.length - 1]
  if (last && last.op === op) last.text += text
  else out.push({ op, text })
}

/**
 * Semantic clean-up: a tiny equality between two changes ("old word" → "new text" shares only the space)
 * is folded into both sides, so the reader sees "~~old word~~ new text" rather than a word-by-word
 * zip; then each change run reads deletions first, insertions second.
 */
function cleanup(segs: DiffSeg[]): DiffSeg[] {
  let cur = segs
  for (let pass = 0; pass < 4; pass++) {
    const out: DiffSeg[] = []
    let folded = false
    for (let i = 0; i < cur.length; i++) {
      const s = cur[i]!
      const prev = cur[i - 1]
      const next = cur[i + 1]
      const between = s.op === 'eq' && prev && next && prev.op !== 'eq' && next.op !== 'eq'
      const small = between && !s.text.includes('\n') && (s.text.trim() === '' || (countWords(s.text) <= 1 && s.text.length <= 4))
      if (small) { out.push({ op: 'del', text: s.text }, { op: 'ins', text: s.text }); folded = true } else out.push({ ...s })
    }
    // regroup each run of changes: all deletions, then all insertions
    const grouped: DiffSeg[] = []
    let del = ''
    let ins = ''
    const flush = () => { pushSeg(grouped, 'del', del); pushSeg(grouped, 'ins', ins); del = ''; ins = '' }
    for (const s of out) {
      if (s.op === 'del') del += s.text
      else if (s.op === 'ins') ins += s.text
      else { flush(); pushSeg(grouped, 'eq', s.text) }
    }
    flush()
    cur = grouped
    if (!folded) break
  }
  return cur
}

/** The word-level diff of two strings: segments whose eq+del texts are `a` and eq+ins texts are `b`. */
export function diffWords(a: string, b: string): DiffSeg[] {
  if (a === b) return a ? [{ op: 'eq', text: a }] : []
  const ta = tokenize(a)
  const tb = tokenize(b)
  // the common head and tail cost nothing
  let head = 0
  while (head < ta.length && head < tb.length && ta[head] === tb[head]) head++
  let tail = 0
  while (tail < ta.length - head && tail < tb.length - head && ta[ta.length - 1 - tail] === tb[tb.length - 1 - tail]) tail++
  const ma = ta.slice(head, ta.length - tail)
  const mb = tb.slice(head, tb.length - tail)
  const out: DiffSeg[] = []
  pushSeg(out, 'eq', ta.slice(0, head).join(''))
  const runs = myers(ma, mb, Object.is, MAX_WORD_EDITS)
  if (!runs) {
    pushSeg(out, 'del', ma.join(''))
    pushSeg(out, 'ins', mb.join(''))
  } else {
    for (const r of runs) {
      const text = (r.op === 'ins' ? mb.slice(r.b, r.b + r.n) : ma.slice(r.a, r.a + r.n)).join('')
      pushSeg(out, r.op, text)
    }
  }
  pushSeg(out, 'eq', ta.slice(ta.length - tail).join(''))
  return cleanup(out)
}

// ---- lines and sections -----------------------------------------------------------------------------

/** Split segments at newlines into lines (a newline ends the line it is on, of whichever side). */
function splitLines(segs: DiffSeg[]): DiffSeg[][] {
  const lines: DiffSeg[][] = [[]]
  for (const s of segs) {
    const parts = s.text.split('\n')
    parts.forEach((p, i) => {
      if (i > 0) lines.push([])
      if (p) pushSeg(lines[lines.length - 1]!, s.op, p)
    })
  }
  return lines
}

function lineKind(segs: DiffSeg[]): DiffLine['kind'] {
  const ops = new Set(segs.map((s) => s.op))
  if (!ops.has('ins') && !ops.has('del')) return 'eq'
  if (!ops.has('eq') && !ops.has('del')) return 'ins'
  if (!ops.has('eq') && !ops.has('ins')) return 'del'
  return 'changed'
}

/** A line's text on one side: the new side (eq + ins) or the old side (eq + del). */
export function sideText(segs: DiffSeg[], side: 'old' | 'new'): string {
  const drop: DiffOp = side === 'new' ? 'del' : 'ins'
  return segs.filter((s) => s.op !== drop).map((s) => s.text).join('')
}

const HEADING = /^(#{1,6})\s+(.*)$/

/** The merged lines of old → new: a line diff, then a word diff inside each changed run of lines. */
export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.replace(/\r\n?/g, '\n').split('\n')
  const b = newText.replace(/\r\n?/g, '\n').split('\n')
  // a text whose lines all changed is a rewrite: removed whole, added whole
  const runs = myers(a, b, Object.is, 2000) ?? [{ op: 'del' as const, a: 0, b: 0, n: a.length }, { op: 'ins' as const, a: a.length, b: 0, n: b.length }]
  const lines: DiffLine[] = []
  const pushLines = (segs: DiffSeg[]) => {
    for (const l of splitLines(segs)) lines.push({ segs: l, kind: lineKind(l) })
  }
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i]!
    if (r.op === 'eq') {
      for (let j = 0; j < r.n; j++) lines.push({ segs: a[r.a + j] ? [{ op: 'eq', text: a[r.a + j]! }] : [], kind: 'eq' })
      continue
    }
    // a changed run: the deleted lines and the inserted lines next to them, diffed word by word
    let delLines: string[] = []
    let insLines: string[] = []
    let j = i
    while (j < runs.length && runs[j]!.op !== 'eq') {
      const rj = runs[j]!
      if (rj.op === 'del') delLines = delLines.concat(a.slice(rj.a, rj.a + rj.n))
      else insLines = insLines.concat(b.slice(rj.b, rj.b + rj.n))
      j++
    }
    i = j - 1
    if (!delLines.length) { for (const l of insLines) lines.push({ segs: l ? [{ op: 'ins', text: l }] : [], kind: 'ins' }); continue }
    if (!insLines.length) { for (const l of delLines) lines.push({ segs: l ? [{ op: 'del', text: l }] : [], kind: 'del' }); continue }
    pushLines(diffWords(delLines.join('\n'), insLines.join('\n')))
  }
  return lines
}

function countSide(lines: DiffLine[], op: 'ins' | 'del'): number {
  let n = 0
  for (const l of lines) for (const s of l.segs) if (s.op === op) n += countWords(s.text)
  return n
}

/** The merged text grouped into its markdown sections, with the +/− word counts. */
export function diffText(oldText: string, newText: string): TextDiff {
  const lines = diffLines(oldText ?? '', newText ?? '')
  const sections: DiffSection[] = [{ title: '', level: 0, lines: [], changed: false, added: 0, removed: 0 }]
  for (const line of lines) {
    const shown = line.kind === 'del' ? sideText(line.segs, 'old') : sideText(line.segs, 'new')
    const h = HEADING.exec(shown)
    if (h) {
      const oldH = HEADING.exec(sideText(line.segs, 'old'))
      const title = h[2]!.trim()
      const oldTitle = line.kind === 'changed' && oldH && oldH[2]!.trim() !== title ? oldH[2]!.trim() : undefined
      sections.push({ title, oldTitle, level: h[1]!.length, lines: [], changed: false, added: 0, removed: 0 })
    }
    sections[sections.length - 1]!.lines.push(line)
  }
  if (!sections[0]!.lines.length) sections.shift()
  for (const s of sections) {
    s.added = countSide(s.lines, 'ins')
    s.removed = countSide(s.lines, 'del')
    s.changed = s.lines.some((l) => l.kind !== 'eq')
  }
  const added = sections.reduce((n, s) => n + s.added, 0)
  const removed = sections.reduce((n, s) => n + s.removed, 0)
  const changedSections = sections.filter((s) => s.changed).length
  return { sections, added, removed, changedSections, changed: changedSections > 0 }
}

/**
 * Which lines of a changed section to show: every changed line, `context` non-blank lines around each,
 * and the heading; the rest fold into gaps ("8 unchanged lines"). Returns runs in line order.
 */
export function hunks(section: DiffSection, context = 2): Array<{ show: boolean; from: number; to: number }> {
  const n = section.lines.length
  const keep = new Array<boolean>(n).fill(false)
  if (section.title && n) keep[0] = true
  const blank = (i: number) => section.lines[i]!.segs.every((s) => !s.text.trim())
  for (let i = 0; i < n; i++) {
    if (section.lines[i]!.kind === 'eq') continue
    keep[i] = true
    for (const dir of [-1, 1]) {
      let left = context
      for (let j = i + dir; j >= 0 && j < n && left > 0; j += dir) {
        keep[j] = true
        if (!blank(j)) left--
      }
    }
  }
  const runs: Array<{ show: boolean; from: number; to: number }> = []
  for (let i = 0; i < n; i++) {
    const last = runs[runs.length - 1]
    if (last && last.show === keep[i]) last.to = i + 1
    else runs.push({ show: keep[i]!, from: i, to: i + 1 })
  }
  // a gap that hides at most one line is no gap worth a button: the line is shown; neighbours merge
  const merged: typeof runs = []
  for (const r of runs) {
    const show = r.show || contentLines(section, r.from, r.to) <= 1
    const last = merged[merged.length - 1]
    if (last && last.show === show) last.to = r.to
    else merged.push({ show, from: r.from, to: r.to })
  }
  return merged
}

/** The non-blank lines of a run (what a gap says it hides). */
export function contentLines(section: DiffSection, from: number, to: number): number {
  let n = 0
  for (let i = from; i < to; i++) if (section.lines[i]!.segs.some((s) => s.text.trim())) n++
  return n
}
